import {
  AppOffer,
  AppOfferGroup,
  AppOfferListItem,
  AppPersonalizedOfferGroup,
  LoyaltyOffer,
  appOfferToLoyaltyOffer,
  isAppOffer,
  isAppOfferGroup,
  isAppPersonalizedOfferGroup,
} from 'state/loyalty/types';
import { blockContentToPlainText } from 'utils/sanity';

export type OffersDisplayedGroupAnalytics = {
  canOnlyChooseOne: boolean;
  childOfferCmsIds: string[];
  childOfferIds: string[];
  cmsId: string;
  groupType: 'AppOfferGroup' | 'AppPersonalizedOfferGroup';
  loyaltyEngineId: string;
  name: string;
  type: string;
  visibleOfferCount: number;
};

type OffersDisplayedAnalytics = {
  attributes: {
    offerCount: number;
    offerGroupCount: number;
    offerGroups: OffersDisplayedGroupAnalytics[];
    visibleItemCount: number;
  };
  standaloneOffers: LoyaltyOffer[];
};

function getItemId(item: unknown, keys: string[]): string {
  const source = item as Record<string, unknown>;
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return '';
}

function getGroupName(group: AppOfferGroup | AppPersonalizedOfferGroup): string {
  return blockContentToPlainText(group.name?.localeRaw);
}

function getChildOfferIds(offers: AppOffer[], keys: string[]): string[] {
  return offers.map(offer => getItemId(offer, keys)).filter(Boolean);
}

function mapOfferGroup(
  group: AppOfferGroup | AppPersonalizedOfferGroup
): OffersDisplayedGroupAnalytics {
  const childOfferIds = getChildOfferIds(group.offers, ['loyaltyEngineId', 'id']);
  const childOfferCmsIds = getChildOfferIds(group.offers, ['cmsId', '_id', 'sanityId']);
  const groupType = isAppPersonalizedOfferGroup(group)
    ? 'AppPersonalizedOfferGroup'
    : 'AppOfferGroup';

  return {
    canOnlyChooseOne: Boolean(group.canOnlyChooseOne),
    childOfferCmsIds,
    childOfferIds,
    cmsId: getItemId(group, ['cmsId', '_id', 'sanityId']),
    groupType,
    loyaltyEngineId: getItemId(group, ['loyaltyEngineId', 'id']),
    name: getGroupName(group),
    type: group.__typename ?? groupType,
    visibleOfferCount: group.offers.length,
  };
}

export function buildOffersDisplayedAnalytics(
  offerListItems: AppOfferListItem[]
): OffersDisplayedAnalytics {
  const standaloneOffers = offerListItems.filter(isAppOffer).map(appOfferToLoyaltyOffer);
  const offerGroups = offerListItems
    .filter((item): item is AppOfferGroup | AppPersonalizedOfferGroup => {
      return isAppOfferGroup(item) || isAppPersonalizedOfferGroup(item);
    })
    .map(mapOfferGroup);

  return {
    standaloneOffers,
    attributes: {
      offerCount: standaloneOffers.length,
      offerGroupCount: offerGroups.length,
      offerGroups,
      visibleItemCount: offerListItems.length,
    },
  };
}
